// All API path segments — used with apiClient (baseURL already set to API_BASE).
// For hooks that need a full URL (e.g. direct fetch/WebSocket), import API_BASE separately.

export const endpoints = {
  auth: {
    token: '/users/token/',
    refresh: '/users/token/refresh/',
    /** Одноразовый тикет для WebSocket. Старый транспорт `?token=<JWT>` бэк
     *  отклоняет на хендшейке (проверено 2026-08-16: 403 против 101). */
    websocketTicket: '/users/websocket-ticket/',
    settings: '/users/settings/',
    settingsPassword: '/users/settings/password/',
    registerParticipant: '/users/register/participant/',
    registerRepresentative: '/users/register/representative/',
    activate: '/users/activate/',
    forgotPassword: '/users/forgot-password/',
    resetPassword: '/users/reset-password/',
    logout: '/users/logout/',
    twoFactorEnable: '/users/2fa/enable/',
    twoFactorVerify: '/users/2fa/verify/',
    twoFactorDisable: '/users/2fa/disable/',
    deleteAccount: '/users/account/delete/',
    /** Согласие участника с текущей версией политики FEMO. Согласие привязано
     *  к версии, а не даётся навсегда: после смены версии его берут заново,
     *  иначе запуск олимпиады отвечает `policy_required`. Только POST —
     *  GET отвечает 405 (проверено на стенде 2026-08-22). */
    participantPolicyConsent: '/users/participant/policy-consent/',
  },
  users: {
    list: '/users/dashboard/',
    detail: (id: number | string) => `/users/dashboard/${id}/`,
    export: '/users/dashboard/export/',
    /** Партнёры страны: `?country_id=<числовой id>`, без него бэк отвечает 400. */
    representatives: '/users/representatives/',
  },
  profile: {
    participant: '/users/participant/profile/',
    participantCityChangeImpact: '/users/participant/profile/city-change-impact/',
    admin: '/users/administrator/profile/',
    representative: '/users/representative/profile/',
  },
  dashboard: {
    summary: '/results/dashboard/summary/',
    current: '/results/dashboard/current/',
    currentStats: '/results/dashboard/current_stats/',
    trend: (period: string) => `/results/dashboard/trend/?period=${period}`,
  },
  participantDashboard: {
    summary: '/users/participant/dashboard/summary/',
    activity: (period: string) => `/users/participant/dashboard/activity/?period=${period}`,
    global: '/users/participant/dashboard/global/',
    globalSearch: (search: string) =>
      `/users/participant/dashboard/global/?search=${encodeURIComponent(search)}`,
    myTasks: '/users/participant/dashboard/my-tasks/',
  },
  notifications: {
    list: '/notifications/',
    approve: (id: number) => `/notifications/${id}/approve/`,
    decline: (id: number) => `/notifications/${id}/decline/`,
  },
  olympiads: {
    list: '/olympiads/dashboard/',
    detail: (id: number | string) => `/olympiads/dashboard/${id}/`,
    download: (id: number | string) => `/olympiads/dashboard/${id}/download/`,
    countries: (id: number | string) => `/olympiads/dashboard/${id}/countries/`,
    /** Регистрации на олимпиаду — не то же, что `/results/`: сюда попадает
     *  каждый записавшийся, а не только те, у кого выгружен результат. */
    participants: (id: number | string) => `/olympiads/dashboard/${id}/participants/`,
    participantFilters: (id: number | string) => `/olympiads/dashboard/${id}/participant-filters/`,
    participantsBulkTime: (id: number | string) =>
      `/olympiads/dashboard/${id}/participants/bulk-time/`,
    participantsDisqualify: (id: number | string, registrationId: number | string) =>
      `/olympiads/dashboard/${id}/participants/${registrationId}/disqualify/`,
    /** Отмена регистрации админом. Физически ничего не удаляется: бэк ставит
     *  статус `cancelled`, возвращает деньги и пишет событие истории. Повтор
     *  по той же регистрации отклоняется (400), чтобы возврат не прошёл дважды. */
    participantsCancel: (id: number | string, registrationId: number | string) =>
      `/olympiads/dashboard/${id}/participants/${registrationId}/`,
    /** Восстановление доступа участника в PROCTOR. Отвечает `202` со
     *  `status: pending` — это постановка команды в очередь, а не
     *  подтверждение самой платформы. */
    participantsProctorReinstate: (id: number | string, registrationId: number | string) =>
      `/olympiads/dashboard/${id}/participants/${registrationId}/proctor-reinstate/`,
    commonList: '/common/olympiads/',
    participantList: '/olympiads/participant/dashboard/',
    participantDetail: (id: number | string) => `/olympiads/participant/dashboard/${id}/`,
    /** Боевой запуск прокторинга. Тело пустое; успех отдаёт одноразовый
     *  `redirect_url`, по которому уходят немедленно. */
    proctorLaunch: (id: number | string) =>
      `/olympiads/participant/dashboard/${id}/proctor-launch/`,
    /** Жизненный цикл сеанса прокторинга — последнее состояние, сохранённое
     *  бэком. Баллов здесь нет; до первого запуска отвечает
     *  `404 proctor_session_not_found`, и это нормальный ответ. */
    proctorStatus: (id: number | string) =>
      `/olympiads/participant/dashboard/${id}/proctor-status/`,
    /** Регистрация участника: списывает кошелёк и создаёт регистрацию одной
     *  транзакцией. Отдельной оплаты через `payments/pay/` больше нет. */
    participantRegister: (id: number | string) =>
      `/olympiads/participant/dashboard/${id}/register/`,
    /** Правка языка и фото до `edit_deadline`. Денег не трогает. */
    participantRegistration: (id: number | string) =>
      `/olympiads/participant/dashboard/${id}/registration/`,
  },
  tasks: {
    list: '/assignments/dashboard/',
    detail: (id: number | string) => `/assignments/dashboard/${id}/`,
    importTemplate: '/assignments/dashboard/import-template/',
    import: '/assignments/dashboard/import/',
    importJob: (jobId: number | string) => `/assignments/dashboard/import-jobs/${jobId}/`,
    // Participant. `general` in the URL means type=preparatory; `daily` is the
    // task of the day. The two id spaces are NOT interchangeable: general takes
    // an Assignment id, daily takes a DailyAssignment id from the daily list.
    participantDaily: '/assignments/participant/dashboard/daily/',
    participantGeneral: '/assignments/participant/dashboard/general/',
    participantDailyDetail: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/daily/`,
    participantDailyStart: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/daily/start/`,
    participantDailySubmit: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/daily/submit/`,
    participantDailyHint: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/daily/hint/`,
    participantDailySolution: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/daily/solution/`,
    participantDailyAnswer: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/daily/answer/`,
    participantGeneralDetail: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/general/`,
    participantGeneralStart: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/general/start/`,
    participantGeneralSubmit: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/general/submit/`,
    participantGeneralHint: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/general/hint/`,
    participantGeneralSolution: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/general/solution/`,
    participantGeneralAnswer: (id: number | string) =>
      `/assignments/participant/dashboard/${id}/general/answer/`,
    /** Mission for one type + grade. Takes `?type=preparatory|daily&grade=N`. */
    participantMission: '/assignments/participant/dashboard/mission/',
  },
  results: {
    list: '/results/dashboard/results/',
    detail: (id: number | string) => `/results/dashboard/results/${id}/`,
    import: '/results/dashboard/results/import/',
    importJob: (jobId: number | string) => `/results/dashboard/results/import-jobs/${jobId}/`,
    export: '/results/dashboard/results/export/',
    summary: '/results/dashboard/results/summary/',
    stats: '/results/dashboard/results/stats/',
    participants: '/results/dashboard/participants/',
    countriesRanking: '/results/dashboard/countries/ranking/',
    countriesCharts: '/results/dashboard/countries/charts/',
    countriesParticipants: '/results/dashboard/countries/participants/',
    countriesParticipantsDetail: (id: number | string) =>
      `/results/dashboard/countries/participants/${id}/`,
    // Participant
    participantRankingSummary: '/results/participant/dashboard/ranking/summary/',
    participantRankingOlympiad: '/results/participant/dashboard/ranking/olympiad/',
    participantRankingGlobal: '/results/participant/dashboard/ranking/global/',
    participantPathProgress: '/results/participant/dashboard/path/progress/',
    participantPathResults: '/results/participant/dashboard/path/results/',
    participantPathAchievements: '/results/participant/dashboard/path/achievements/',
    participantCertDownload: (olympiadPk: number | string) =>
      `/results/participant/dashboard/path/certificates/${olympiadPk}/download/`,
    // Representative
    repStats: '/results/representatives/dashboard/stats/',
    repCurrentOlympiad: '/results/representatives/dashboard/current-olympiad/',
    repParticipants: '/results/representatives/dashboard/participants/',
    repParticipantsExport: '/results/representatives/dashboard/participants/export/',
    repParticipantDelete: (id: number | string) =>
      `/results/representatives/dashboard/participants/${id}/`,
    repParticipantProfile: (id: number | string) =>
      `/results/representatives/dashboard/participants/${id}/profile/`,
    repParticipantProgress: (id: number | string) =>
      `/results/representatives/dashboard/participants/${id}/progress/`,
    repParticipantAchievements: (id: number | string) =>
      `/results/representatives/dashboard/participants/${id}/achievements/`,
    repParticipantResults: (id: number | string) =>
      `/results/representatives/dashboard/participants/${id}/results/`,
    repParticipantPayments: (id: number | string) =>
      `/results/representatives/dashboard/participants/${id}/payments/`,
    repParticipantCertificateDownload: (id: number | string, olympiadPk: number | string) =>
      `/results/representatives/dashboard/participants/${id}/certificates/${olympiadPk}/download/`,
    repDistribution: '/results/representatives/dashboard/participants/distribution/',
    repRanking: '/results/representatives/dashboard/participants/ranking/',
    repRankingDownload: '/results/representatives/dashboard/participants/ranking/download/',
    repDynamics: '/results/representatives/dashboard/participants/dynamics/',
    repSummary: '/results/representatives/dashboard/participants/summary/',
    repOlympiadSummary: (id: number | string) =>
      `/results/representative/dashboard/olympiads/${id}/summary/`,
    repOlympiadRanking: (id: number | string) =>
      `/results/representative/dashboard/olympiads/${id}/ranking/`,
    repOlympiadRankingDownload: (id: number | string) =>
      `/results/representative/dashboard/olympiads/${id}/ranking/download/`,
  },
  payments: {
    list: '/payments/dashboard/',
    detail: (id: number | string) => `/payments/dashboard/${id}/`,
    download: (id: number | string) => `/payments/dashboard/${id}/download/`,
    participantBalance: '/payments/participant/dashboard/balance/',
    participantHistory: '/payments/participant/dashboard/history/',
    participantTopup: '/payments/participant/dashboard/topup/',
    participantDownload: (id: number | string) => `/payments/participant/dashboard/${id}/download/`,
  },
  certificates: {
    list: '/certificates/dashboard/',
    download: (id: number | string) => `/certificates/dashboard/${id}/download/`,
    participantList: '/certificates/participant/dashboard/',
    participantDetail: (id: number | string) => `/certificates/participant/dashboard/${id}/`,
    participantDownload: (id: number | string) =>
      `/certificates/participant/dashboard/${id}/download/`,
  },
  common: {
    countries: '/common/countries/',
    olympiads: '/common/olympiads/',
  },
  locations: {
    countries: '/locations/countries/',
    cities: '/locations/cities/',
  },
  chat: {
    representatives: '/chats/representatives/',
    representativesSearch: (q: string) => `/chats/representatives/?search=${encodeURIComponent(q)}`,
    administrators: '/chats/administrators/',
    privateMessages: (profileId: number) => `/chats/private/${profileId}/messages/`,
    announcementAttachments: '/chats/rooms/1/attachments/',
    privateAttachments: (roomId: number) => `/chats/private/${roomId}/attachments/`,
    groupAttachments: (roomId: number) => `/chats/rooms/${roomId}/attachments/`,

    /** Тех. поддержка участника. Активный тикет ровно один: попытка завести
     *  второй отклоняется с 409 (проверено на стенде 2026-08-21). */
    support: {
      /** GET — `{active_ticket}` либо `{active_ticket: null}`; POST — создание. */
      active: '/chats/participant/support/',
      /** Сообщения активного тикета; POST — ответ в него же. */
      activeMessages: '/chats/participant/support/messages/',
      activeRead: '/chats/participant/support/read/',
      /** История тикетов, пагинированная; `?status=` фильтрует. */
      tickets: '/chats/participant/support/tickets/',
      ticket: (id: number) => `/chats/participant/support/tickets/${id}/`,
      ticketMessages: (id: number) => `/chats/participant/support/tickets/${id}/messages/`,
      ticketRead: (id: number) => `/chats/participant/support/tickets/${id}/read/`,
      /** Файл сообщения. Путь собираем из id, а не берём готовую ссылку из
       *  поля `file`: бэк отдаёт её по `http://` (смешанный контент на
       *  https-странице) и требует Bearer — по такой ссылке браузер получает
       *  401 (проверено на стенде 2026-08-21). Чужой файл отдаётся как 404,
       *  а не 403: интерфейс не должен подсказывать, что он существует. */
      messageAttachment: (roomId: number, messageId: number) =>
        `/chats/support/tickets/${roomId}/messages/${messageId}/attachment/`,
    },

    /** Инбокс тех. поддержки у администратора. Комната и тикет — один и тот же
     *  идентификатор, как и у участника. */
    adminSupport: {
      rooms: '/chats/administrator/support/rooms/',
      room: (id: number) => `/chats/administrator/support/rooms/${id}/`,
      roomMessages: (id: number) => `/chats/administrator/support/rooms/${id}/messages/`,
      roomRead: (id: number) => `/chats/administrator/support/rooms/${id}/read/`,
      /** Смена статуса. Повтор текущего идемпотентен, переоткрытие закрытого — 409. */
      roomStatus: (id: number) => `/chats/administrator/support/rooms/${id}/status/`,
    },
  },
} as const
